import { DeviceType, DEVICE_DISPLAY_NAMES } from '../constants/device';

export interface DeviceInfo {
  type: DeviceType;
  userAgent: string;
  screenWidth: number;
  screenHeight: number;
  pixelRatio: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isUltrawide: boolean;
}

class DeviceDetectionService {
  /**
   * Detects the current device type based on user agent and screen dimensions
   */
  detectDevice(): DeviceInfo {
    const userAgent = navigator.userAgent.toLowerCase();
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const pixelRatio = window.devicePixelRatio || 1;

    // Calculate actual screen dimensions accounting for pixel ratio
    const actualWidth = screenWidth * pixelRatio;
    const actualHeight = screenHeight * pixelRatio;

    // Device type detection logic
    const isMobile = this.isMobileDevice(userAgent, actualWidth, actualHeight);
    const isTablet = this.isTabletDevice(userAgent, actualWidth, actualHeight);
    const isUltrawide = this.isUltrawideDevice(actualWidth, actualHeight);
    const isDesktop = !isMobile && !isTablet;

    let deviceType: DeviceType;
    if (isMobile) {
      // Detect specific mobile device types
      if (userAgent.includes('iphone')) {
        // Check for iPhone Pro models
        if (userAgent.includes('iphone15') || userAgent.includes('iphone14') || userAgent.includes('pro')) {
          deviceType = DeviceType.IPHONE_PRO;
        } else {
          deviceType = DeviceType.IPHONE;
        }
      } else if (userAgent.includes('android')) {
        deviceType = DeviceType.ANDROID;
      } else {
        deviceType = DeviceType.MOBILE_PHONE;
      }
    } else if (isTablet) {
      deviceType = DeviceType.TABLET;
    } else if (isUltrawide) {
      deviceType = DeviceType.ULTRAWIDE;
    } else {
      // Detect specific desktop types
      if (userAgent.includes('macintosh') || userAgent.includes('mac os')) {
        deviceType = DeviceType.LAPTOP;
      } else {
        deviceType = DeviceType.DESKTOP;
      }
    }

    return {
      type: deviceType,
      userAgent,
      screenWidth: actualWidth,
      screenHeight: actualHeight,
      pixelRatio,
      isMobile,
      isTablet,
      isDesktop,
      isUltrawide
    };
  }

  /**
   * Gets the optimal device type for downloading based on current device
   */
  getOptimalDownloadDevice(): DeviceType {
    const deviceInfo = this.detectDevice();
    return deviceInfo.type;
  }

  /**
   * Checks if a wallpaper pack has a variation for the current device
   */
  hasVariationForCurrentDevice(deviceVariations: Record<string, any>): boolean {
    const currentDevice = this.getOptimalDownloadDevice();
    return currentDevice in deviceVariations;
  }

  /**
   * Gets the best available device variation for the current device
   * Falls back to other compatible devices if exact match not available
   */
  getBestAvailableDevice(deviceVariations: Record<string, any>): DeviceType | null {
    const currentDevice = this.getOptimalDownloadDevice();
    
    // First try exact match
    if (currentDevice in deviceVariations) {
      return currentDevice;
    }

    // Fallback logic based on device compatibility
    const availableDevices = Object.keys(deviceVariations) as DeviceType[];
    
    switch (currentDevice) {
      case DeviceType.MOBILE_PHONE:
      case DeviceType.IPHONE:
      case DeviceType.IPHONE_PRO:
      case DeviceType.ANDROID:
        // Mobile devices can use other mobile types or tablet as fallback
        return availableDevices.find(d => [DeviceType.IPHONE, DeviceType.IPHONE_PRO, DeviceType.ANDROID, DeviceType.MOBILE_PHONE].includes(d)) ||
               availableDevices.find(d => d === DeviceType.TABLET) || 
               availableDevices[0] || null;
               
      case DeviceType.TABLET:
        // Tablet can use mobile or desktop as fallback
        return availableDevices.find(d => [DeviceType.MOBILE_PHONE, DeviceType.IPHONE, DeviceType.ANDROID].includes(d)) ||
               availableDevices.find(d => [DeviceType.DESKTOP, DeviceType.LAPTOP].includes(d)) ||
               availableDevices[0] || null;
               
      case DeviceType.DESKTOP:
      case DeviceType.LAPTOP:
        // Desktop/Laptop can use each other or ultrawide as fallback
        return availableDevices.find(d => [DeviceType.DESKTOP, DeviceType.LAPTOP].includes(d)) ||
               availableDevices.find(d => d === DeviceType.ULTRAWIDE) ||
               availableDevices[0] || null;
               
      case DeviceType.ULTRAWIDE:
        // Ultrawide can use desktop/laptop as fallback
        return availableDevices.find(d => [DeviceType.DESKTOP, DeviceType.LAPTOP].includes(d)) ||
               availableDevices[0] || null;
               
      case DeviceType.TV:
        // TV can use desktop/ultrawide as fallback
        return availableDevices.find(d => [DeviceType.ULTRAWIDE, DeviceType.DESKTOP].includes(d)) ||
               availableDevices[0] || null;
               
      case DeviceType.WATCH:
        // Watch can use mobile as fallback
        return availableDevices.find(d => [DeviceType.MOBILE_PHONE, DeviceType.IPHONE, DeviceType.ANDROID].includes(d)) ||
               availableDevices[0] || null;
               
      default:
        return availableDevices[0] || null;
    }
  }

  /**
   * Gets device-specific download recommendations
   */
  getDownloadRecommendations(deviceVariations: Record<string, any>): {
    recommended: DeviceType | null;
    alternatives: DeviceType[];
    message: string;
  } {
    const currentDevice = this.getOptimalDownloadDevice();
    const availableDevices = Object.keys(deviceVariations) as DeviceType[];
    
    if (currentDevice in deviceVariations) {
      return {
        recommended: currentDevice,
        alternatives: availableDevices.filter(d => d !== currentDevice),
        message: `Optimized for your ${this.getDeviceDisplayName(currentDevice)}`
      };
    }

    const bestAlternative = this.getBestAvailableDevice(deviceVariations);
    return {
      recommended: bestAlternative,
      alternatives: availableDevices.filter(d => d !== bestAlternative),
      message: bestAlternative 
        ? `Recommended for your device: ${this.getDeviceDisplayName(bestAlternative)}`
        : 'No optimal match found'
    };
  }

  /**
   * Gets user-friendly device display name
   */
  getDeviceDisplayName(deviceType: DeviceType): string {
    return DEVICE_DISPLAY_NAMES[deviceType] || deviceType;
  }

  private isMobileDevice(userAgent: string, width: number, height: number): boolean {
    // Check user agent for mobile indicators
    const mobileKeywords = [
      'mobile', 'android', 'iphone', 'ipod', 'blackberry', 
      'windows phone', 'opera mini', 'iemobile'
    ];
    
    const hasMobileUA = mobileKeywords.some(keyword => userAgent.includes(keyword));
    
    // Check screen dimensions (mobile typically has smaller screens)
    const maxDimension = Math.max(width, height);
    const minDimension = Math.min(width, height);
    const isSmallScreen = maxDimension <= 1920 && minDimension <= 1080;
    
    // Check aspect ratio (mobile typically portrait or close to square)
    const aspectRatio = maxDimension / minDimension;
    const isMobileAspectRatio = aspectRatio >= 1.5 && aspectRatio <= 2.5;
    
    return hasMobileUA || (isSmallScreen && isMobileAspectRatio);
  }

  private isTabletDevice(userAgent: string, width: number, height: number): boolean {
    // Check user agent for tablet indicators
    const tabletKeywords = ['ipad', 'tablet', 'kindle', 'playbook', 'silk'];
    const haTabletUA = tabletKeywords.some(keyword => userAgent.includes(keyword));
    
    // Check screen dimensions (tablet typically medium-sized screens)
    const maxDimension = Math.max(width, height);
    const minDimension = Math.min(width, height);
    const isTabletScreen = maxDimension >= 1024 && maxDimension <= 2732 && 
                          minDimension >= 768 && minDimension <= 2048;
    
    // Check aspect ratio (tablet typically between mobile and desktop ratios)
    const aspectRatio = maxDimension / minDimension;
    const isTabletAspectRatio = aspectRatio >= 1.2 && aspectRatio <= 1.8;
    
    return haTabletUA || (isTabletScreen && isTabletAspectRatio && !this.isMobileDevice(userAgent, width, height));
  }

  private isUltrawideDevice(width: number, height: number): boolean {
    // Ultrawide monitors typically have very wide aspect ratios
    const aspectRatio = Math.max(width, height) / Math.min(width, height);
    const isUltrawideRatio = aspectRatio >= 2.1; // 21:9 or wider
    
    // Also check for common ultrawide resolutions
    const ultrawideResolutions = [
      { w: 3440, h: 1440 }, // 21:9 QHD
      { w: 5120, h: 1440 }, // 32:9 QHD
      { w: 2560, h: 1080 }, // 21:9 FHD
      { w: 3840, h: 1600 }, // 24:10 4K
    ];
    
    const hasUltrawideResolution = ultrawideResolutions.some(res => 
      (width === res.w && height === res.h) || (width === res.h && height === res.w)
    );
    
    return isUltrawideRatio || hasUltrawideResolution;
  }
}

export const deviceDetectionService = new DeviceDetectionService();